/**
 * Builds a single PMS reading as a formatted A4-portrait "certificate" PDF —
 * letterhead + identity block + a numbered checklist table + QR codes linking
 * to any photo/video evidence + an inspector/engineer sign-off block. This is
 * a different document from the Daily Log (one table of many readings,
 * landscape) — matches the paper PMS report format this replaces.
 */
import { qrDataUrl } from '../components/QrCode';

type Row = Record<string, unknown>;
interface PmsField { key: string; label: string; unit: string; dataType: string; col?: string }

export interface PmsReportPdfInput {
  org?: { name?: string; subtitle?: string };
  logoDataUrl?: string;
  templateTitle: string;
  equipName: string;
  equipLoc: string;
  equipmentId: string;
  submissionId: string;
  effectiveReadingAt: string; // ISO
  operatorName: string;
  operatorId: string;
  remarks: string;
  approvedByName?: string;
  approvedAt?: string; // ISO
  fields: PmsField[]; // flat, in report order
  row: Row; // the raw reading row, keyed by column name
  manufacturer?: string;
  pmsNumber?: string;
  uniqueId?: string;
  yearOfInstallation?: string;
  serialNumber?: string;
  capacity?: string;
  pmsDueAt?: string; // ISO — this reading's effective time + the equipment's target interval
}

const NAME = 'U. N. Mehta Institute of Cardiology & Research Centre';
const SUB = 'Affiliated to B. J. Medical College, Ahmedabad-380016, INDIA';

function fmtDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString([], { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function cellValue(row: Row, f: PmsField): string {
  const v = f.col ? row[f.col] : undefined;
  return v === undefined || v === null || v === '' ? '' : String(v);
}

export async function buildPmsReportPdf(input: PmsReportPdfInput): Promise<Blob> {
  const { jsPDF } = await import('jspdf');
  const autoTable = (await import('jspdf-autotable')).default;

  const checklistFields = input.fields.filter((f) => f.dataType !== 'photo' && f.dataType !== 'video');
  const mediaFields = input.fields.filter((f) => f.dataType === 'photo' || f.dataType === 'video');

  // QR codes first (async) so the rest of the layout is synchronous.
  const mediaWithQr: Array<{ label: string; url: string; qr: string }> = [];
  for (const f of mediaFields) {
    const url = cellValue(input.row, f);
    if (!url) continue;
    try { mediaWithQr.push({ label: f.label, url, qr: await qrDataUrl(url, 300) }); } catch { /* skip a bad one */ }
  }

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const M = 12;
  const org = input.org ?? {};

  if (input.logoDataUrl) {
    try { doc.addImage(input.logoDataUrl, 'PNG', M, M, 20, 13); } catch { /* bad image */ }
  }
  doc.setTextColor(18, 58, 134);
  doc.setFont('helvetica', 'bold'); doc.setFontSize(13);
  doc.text(org.name ?? NAME, pageW / 2, M + 5, { align: 'center' });
  doc.setFont('helvetica', 'normal'); doc.setFontSize(8);
  doc.text(`(${org.subtitle ?? SUB})`, pageW / 2, M + 10, { align: 'center' });
  doc.setDrawColor(18, 58, 134); doc.setLineWidth(0.5);
  doc.line(M, M + 13, pageW - M, M + 13);

  doc.setTextColor(17, 17, 17);
  doc.setFont('helvetica', 'bold'); doc.setFontSize(12);
  doc.text(input.templateTitle, pageW / 2, M + 20, { align: 'center' });

  // ---- identity block (mirrors the paper PMS report's nameplate header) ----
  const dash = (v?: string) => (v && v.trim() ? v : '—');
  autoTable(doc, {
    startY: M + 25,
    margin: { left: M, right: M },
    body: [
      ['Equipment', input.equipName, 'Equipment ID', input.equipmentId],
      ['Manufacturer', dash(input.manufacturer), 'Report reference', input.submissionId.slice(0, 8).toUpperCase()],
      ['PMS / asset number', dash(input.pmsNumber), 'Unique ID', dash(input.uniqueId)],
      ['Serial number', dash(input.serialNumber), 'Capacity', dash(input.capacity)],
      ['Location / unit name', { content: input.equipLoc, colSpan: 3 }],
      ['Year of installation', dash(input.yearOfInstallation), 'PMS done date & time', fmtDateTime(input.effectiveReadingAt)],
      ['PMS due date', input.pmsDueAt ? fmtDateTime(input.pmsDueAt) : '—', '', ''],
    ] as never,
    theme: 'grid',
    styles: { fontSize: 8, cellPadding: 1.6, lineColor: [0, 0, 0], lineWidth: 0.15, textColor: [17, 17, 17] },
    columnStyles: { 0: { fontStyle: 'bold', fillColor: [240, 243, 250], cellWidth: 32 }, 2: { fontStyle: 'bold', fillColor: [240, 243, 250], cellWidth: 32 } },
  });

  // ---- checklist ----
  const afterIdentity = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY;
  autoTable(doc, {
    startY: afterIdentity + 4,
    margin: { left: M, right: M, bottom: 24 },
    head: [['Sr No', 'Points', 'Action', 'Remarks']],
    body: checklistFields.map((f, i) => {
      const v = cellValue(input.row, f);
      return [String(i + 1), f.unit ? `${f.label} (${f.unit})` : f.label, v || '—', ''];
    }),
    theme: 'grid',
    styles: { fontSize: 8, cellPadding: 1.6, lineColor: [0, 0, 0], lineWidth: 0.15, textColor: [17, 17, 17] },
    headStyles: { fillColor: [223, 230, 242], textColor: [17, 17, 17], fontStyle: 'bold' },
    columnStyles: { 0: { cellWidth: 8, halign: 'center' }, 2: { cellWidth: 28, halign: 'center' }, 3: { cellWidth: 34 } },
  });

  let y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 6;
  const pageH = doc.internal.pageSize.getHeight();

  if (input.remarks) {
    if (y > pageH - 40) { doc.addPage(); y = M; }
    doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(17, 17, 17);
    doc.text('Observation:', M, y);
    doc.setFont('helvetica', 'normal');
    const wrapped = doc.splitTextToSize(input.remarks, pageW - M * 2);
    doc.text(wrapped, M, y + 5);
    y += 5 + wrapped.length * 4.2 + 4;
  }

  // ---- media QR grid ----
  if (mediaWithQr.length) {
    const qrSize = 26, gap = 6, cols = 4;
    const blockH = qrSize + 10;
    if (y > pageH - blockH - 24) { doc.addPage(); y = M; }
    doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(17, 17, 17);
    doc.text('Scan for photo / video evidence:', M, y);
    y += 4;
    mediaWithQr.forEach((m, i) => {
      const col = i % cols;
      const x = M + col * (qrSize + gap);
      if (col === 0 && i > 0) y += blockH;
      try { doc.addImage(m.qr, 'PNG', x, y, qrSize, qrSize); } catch { /* skip */ }
      doc.setFont('helvetica', 'normal'); doc.setFontSize(6.5);
      const label = doc.splitTextToSize(m.label, qrSize + 2);
      doc.text(label, x + qrSize / 2, y + qrSize + 3.5, { align: 'center' });
    });
    y += blockH + 4;
  }

  // ---- sign-off ----
  if (y > pageH - 30) { doc.addPage(); y = M; }
  const signY = Math.max(y + 6, pageH - 28);
  const half = (pageW - M * 2) / 2;
  doc.setDrawColor(51, 51, 51); doc.setLineWidth(0.3);
  doc.line(M, signY, M + half - 8, signY);
  doc.line(M + half + 8, signY, pageW - M, signY);
  doc.setFont('helvetica', 'bold'); doc.setFontSize(8); doc.setTextColor(34, 34, 34);
  doc.text('Inspection by', M, signY + 4);
  doc.text('Checked by (UNMICRC Engineer)', M + half + 8, signY + 4);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5);
  doc.text(`${input.operatorName}  (${input.operatorId})`, M, signY + 8.5);
  doc.text(fmtDateTime(input.effectiveReadingAt), M, signY + 12.5);
  if (input.approvedByName && input.approvedAt) {
    doc.text(input.approvedByName, M + half + 8, signY + 8.5);
    doc.text(fmtDateTime(input.approvedAt), M + half + 8, signY + 12.5);
  } else {
    doc.setTextColor(150, 150, 150);
    doc.text('Pending review', M + half + 8, signY + 8.5);
  }

  return doc.output('blob');
}
